import { FC } from 'react';
import { css } from '@emotion/react';
import { colors, spacing, fontStyles } from '../../../styles';
import { ToolRendererProps } from './types';
import { LoadingState } from './shared/LoadingState';
import { DataCard } from './shared/DataCard';

interface PositionSizing {
  recommended_pct?: number;
  dollar_amount?: number;
  kelly_optimal_pct?: number;
  risk_reward_ratio?: number;
}

interface RiskMetrics {
  risk_reward_ratio?: number;
  max_loss?: number;
  max_loss_dollars?: number;
  max_loss_pct?: number;
  max_loss_pct_of_portfolio?: number;
}

interface Assumptions {
  conviction?: number;
  win_probability?: number;
  bull_return?: number;
  bear_return?: number;
  portfolio_value?: number;
  kelly_fraction?: number;
}

interface PositionSizeOutput {
  position_sizing?: PositionSizing;
  risk_metrics?: RiskMetrics;
  assumptions?: Assumptions;
  valuation_context?: {
    symbol?: string;
    embedded_expectations?: Record<string, unknown>;
  };
  warnings?: string[];
  caveat?: string;
  error?: string;
}

const formatDollars = (value: number): string => {
  return `$${value.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
};

export const PositionSizeRenderer: FC<ToolRendererProps> = ({ output, isLoading }) => {
  if (isLoading) {
    return <LoadingState message="Calculating position size..." />;
  }

  const data = output as PositionSizeOutput;

  if (data.error) {
    return (
      <div
        css={css`
          ${fontStyles.cat}
          color: ${colors.berry};
        `}
      >
        Error: {data.error}
      </div>
    );
  }

  const sizing = data.position_sizing;
  const risk = data.risk_metrics;
  const assumptions = data.assumptions;
  const symbol = data.valuation_context?.symbol;

  if (!sizing) {
    return (
      <div
        css={css`
          ${fontStyles.cat}
          color: ${colors.silver};
        `}
      >
        Position size calculation completed
      </div>
    );
  }

  return (
    <DataCard>
      <div
        css={css`
          ${fontStyles.cat}
          color: ${colors.liquorice};

          html[data-theme='dark'] & {
            color: ${colors.sugar};
          }
        `}
      >
        {/* Symbol */}
        {symbol && (
          <div
            css={css`
              ${fontStyles.flea}
              color: ${colors.silver};
              text-transform: uppercase;
              letter-spacing: 0.5px;
              margin-bottom: ${spacing.centi};

              html[data-theme='dark'] & {
                color: ${colors.tungsten};
              }
            `}
          >
            {symbol} Position Size
          </div>
        )}

        {/* Recommended allocation - prominent */}
        <div
          css={css`
            margin-bottom: ${spacing.centi};
          `}
        >
          <span
            css={css`
              ${fontStyles.shark}
              font-weight: 600;
              color: ${colors.petrolBlue};

              html[data-theme='dark'] & {
                color: ${colors.skyBlue};
              }
            `}
          >
            {sizing.recommended_pct?.toFixed(1)}%
          </span>
          {sizing.dollar_amount !== undefined && (
            <span
              css={css`
                ${fontStyles.cat}
                color: ${colors.silver};
                margin-left: ${spacing.centi};

                html[data-theme='dark'] & {
                  color: ${colors.tungsten};
                }
              `}
            >
              ({formatDollars(sizing.dollar_amount)})
            </span>
          )}
        </div>

        {/* Details grid */}
        <div
          css={css`
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: ${spacing.milli} ${spacing.deci};
            font-family: monospace;
            font-size: 12px;
            line-height: 1.6;
          `}
        >
          {(risk?.risk_reward_ratio ?? sizing.risk_reward_ratio) !== undefined && (
            <>
              <span
                css={css`
                  color: ${colors.silver};
                  html[data-theme='dark'] & {
                    color: ${colors.tungsten};
                  }
                `}
              >
                Risk/Reward
              </span>
              <span>{(risk?.risk_reward_ratio ?? sizing.risk_reward_ratio)!.toFixed(1)}x</span>
            </>
          )}

          {sizing.kelly_optimal_pct !== undefined && (
            <>
              <span
                css={css`
                  color: ${colors.silver};
                  html[data-theme='dark'] & {
                    color: ${colors.tungsten};
                  }
                `}
              >
                Kelly Optimal
              </span>
              <span>
                {sizing.kelly_optimal_pct.toFixed(1)}%
                {sizing.recommended_pct !== undefined && sizing.kelly_optimal_pct > sizing.recommended_pct && (
                  <span
                    css={css`
                      color: ${colors.silver};
                      html[data-theme='dark'] & {
                        color: ${colors.tungsten};
                      }
                    `}
                  >
                    {' '}
                    (capped)
                  </span>
                )}
              </span>
            </>
          )}

          {(risk?.max_loss ?? risk?.max_loss_dollars) !== undefined && (
            <>
              <span
                css={css`
                  color: ${colors.silver};
                  html[data-theme='dark'] & {
                    color: ${colors.tungsten};
                  }
                `}
              >
                Max Loss
              </span>
              <span
                css={css`
                  color: ${colors.berry};
                `}
              >
                {formatDollars((risk?.max_loss ?? risk?.max_loss_dollars)!)}
                {(risk?.max_loss_pct ?? risk?.max_loss_pct_of_portfolio) !== undefined &&
                  ` (${(risk?.max_loss_pct ?? risk?.max_loss_pct_of_portfolio)!.toFixed(2)}%)`}
              </span>
            </>
          )}

          {assumptions?.conviction !== undefined && (
            <>
              <span
                css={css`
                  color: ${colors.silver};
                  html[data-theme='dark'] & {
                    color: ${colors.tungsten};
                  }
                `}
              >
                Conviction
              </span>
              <span>{assumptions.conviction}/10</span>
            </>
          )}

          {assumptions?.win_probability !== undefined && (
            <>
              <span
                css={css`
                  color: ${colors.silver};
                  html[data-theme='dark'] & {
                    color: ${colors.tungsten};
                  }
                `}
              >
                Win Probability
              </span>
              <span>{(assumptions.win_probability * 100).toFixed(1)}%</span>
            </>
          )}

          {assumptions?.bull_return !== undefined && assumptions?.bear_return !== undefined && (
            <>
              <span
                css={css`
                  color: ${colors.silver};
                  html[data-theme='dark'] & {
                    color: ${colors.tungsten};
                  }
                `}
              >
                Bull / Bear
              </span>
              <span>
                <span
                  css={css`
                    color: ${colors.leaf};
                  `}
                >
                  +{(assumptions.bull_return * 100).toFixed(0)}%
                </span>
                {' / '}
                <span
                  css={css`
                    color: ${colors.berry};
                  `}
                >
                  {(assumptions.bear_return * 100).toFixed(0)}%
                </span>
              </span>
            </>
          )}
        </div>

        {/* Warnings */}
        {data.warnings && data.warnings.length > 0 && (
          <div
            css={css`
              margin-top: ${spacing.centi};
              display: flex;
              flex-direction: column;
              gap: ${spacing.milli};
            `}
          >
            {data.warnings.map((warning: string, i: number) => (
              <div
                key={i}
                css={css`
                  ${fontStyles.flea}
                  color: ${colors.earth};
                  line-height: 1.4;
                `}
              >
                {warning}
              </div>
            ))}
          </div>
        )}

        {/* Caveat */}
        {data.caveat && (
          <div
            css={css`
              ${fontStyles.flea}
              margin-top: ${spacing.centi};
              color: ${colors.silver};
              line-height: 1.4;

              html[data-theme='dark'] & {
                color: ${colors.tungsten};
              }
            `}
          >
            {data.caveat}
          </div>
        )}
      </div>
    </DataCard>
  );
};
